// Soft-warn helpers for the Diagnostics (6A–6F) tab. Each lives here rather
// than next to its use site so the inputs and outputs are easy to test.

// Levenshtein edit distance — used by the 6F "Likely typo?" soft-warn so a
// 1-character edit on a proper-noun ground truth (e.g. Verde River → Verde
// Riverr) shows up as a curator-review hint rather than a silent change.
export function levenshteinDistance(a, b) {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;
  const m = a.length;
  const n = b.length;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

export function looksLikeProperNoun(s) {
  return typeof s === 'string' && /^[A-Z][a-z]/.test(s.trim());
}

// Soft-warn when an edit between two values is a single-character difference
// AND at least one side reads as a proper noun. Skips numeric edits like
// "12 PM" → "1 PM" where a 1-char delta is normal, not a typo.
export function isLikelyTypoEdit(oldValue, newValue) {
  if (oldValue == null || newValue == null) return false;
  const a = String(oldValue).trim();
  const b = String(newValue).trim();
  if (!a || !b || a === b) return false;
  if (!looksLikeProperNoun(a) && !looksLikeProperNoun(b)) return false;
  if (Math.abs(a.length - b.length) > 1) return false;
  return levenshteinDistance(a.toLowerCase(), b.toLowerCase()) === 1;
}

// Display-only normalization for known canonical proper nouns whose stored
// values are missing punctuation or use inconsistent casing. The underlying
// record stays unchanged — edits and API writes still see the raw value —
// but read-only renders use the official spelling so users don't read the
// dashboard as authoritative for the wrong form.
const CANONICAL_FACT_VALUE_FIXES = [
  // Tripadvisor's official award name is "Travelers' Choice" (with apostrophe);
  // existing records store it without one.
  { match: /\bTravelers Choice\b/g, replace: "Travelers' Choice" },
  // Property-type values are written with mixed casing in the ground-truth
  // table ("RV resort", "RV park"). Title-case the second word so they read
  // as proper noun phrases (markup #84). The regex uses \b boundaries so
  // it won't disturb "RV resort-adjacent" or other compound text.
  { match: /\bRV resort\b/g, replace: 'RV Resort' },
  { match: /\bRV park\b/g, replace: 'RV Park' },
  { match: /\bRV campground\b/g, replace: 'RV Campground' },
];

export function canonicalizeFactValue(value) {
  if (value == null) return value;
  let out = String(value);
  for (const { match, replace } of CANONICAL_FACT_VALUE_FIXES) {
    out = out.replace(match, replace);
  }
  return out;
}

// Convert boolean-like fact values to "Yes" / "No" for customer-facing copy.
// Free-text values (e.g. "specialty outdoor kitchen") pass through unchanged
// after canonicalization. Markup #112 — boolean facts must read as Yes/No
// everywhere they surface, not as raw "true" / "false" strings.
export function prettifyBooleanValue(value) {
  if (value == null) return value;
  const raw = typeof value === 'boolean' ? String(value) : String(value).trim();
  const norm = raw.toLowerCase();
  if (norm === 'true' || norm === 'yes' || norm === 't') return 'Yes';
  if (norm === 'false' || norm === 'no' || norm === 'f') return 'No';
  return canonicalizeFactValue(value);
}

// Detect fact_keys that should hold boolean values. Backend currently allows
// providers (and ground-truth records) to answer with free-text (e.g.
// "specialty outdoor kitchen" for `has_outdoor_kitchen`), which breaks
// accuracy comparison against a yes/no answer. The frontend surfaces a
// separate "text vs yes/no" badge so reviewers don't read the consensus % as
// real signal for these rows.
const BOOL_FACT_KEY = /^(has|is|allows|accepts|offers|supports|requires)_/i;

export function isBooleanFactKey(key) {
  return typeof key === 'string' && BOOL_FACT_KEY.test(key);
}

export function isBooleanLikeText(value) {
  if (value == null) return false;
  const s = String(value).trim().toLowerCase();
  return ['true', 'false', 'yes', 'no', 't', 'f'].includes(s);
}

export function isTypeMismatch(factKey, statedValue) {
  return isBooleanFactKey(factKey)
    && statedValue != null
    && String(statedValue).trim() !== ''
    && !isBooleanLikeText(statedValue);
}

// Detect when an AI's answer points the wrong direction for a min/max fact —
// e.g. fact_key `max_rv_length` but the AI said "minimum 25–30 ft RV". The
// value contradiction badge alone reads as "Grok got the number wrong" when
// the real issue is Grok answered the opposite question.
const MAX_FACT_KEY = /^max_/i;
const MIN_FACT_KEY = /^min_/i;
const MIN_WORD = /\b(minimum|min|smallest|shortest|lowest|least)\b/i;
const MAX_WORD = /\b(maximum|max|largest|longest|highest|most)\b/i;

export function isMinMaxConfusion(factKey, statedValue) {
  if (typeof factKey !== 'string' || statedValue == null) return false;
  const s = String(statedValue);
  if (!s.trim()) return false;
  if (MAX_FACT_KEY.test(factKey) && MIN_WORD.test(s) && !MAX_WORD.test(s)) return true;
  if (MIN_FACT_KEY.test(factKey) && MAX_WORD.test(s) && !MIN_WORD.test(s)) return true;
  return false;
}
